import { useEffect, useRef } from 'react'
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom'

/**
 * Lets a single-address screen answer the browser's back button itself.
 *
 * WHY THIS EXISTS (#46): a session is one address, `/session`, with its steps
 * held in page state. The browser knows nothing about those steps, so its back
 * button (or the back button on a mouse) left the page mid-session. To someone
 * using the app while upset, that looked like losing everything.
 *
 * HOW IT WORKS: the screen keeps one spare history entry on top of the entry it
 * was opened with. Both have the same address; the spare one is marked with
 * `backGuard: true` in its navigation state.
 *
 *     [ ...earlier pages, /session (opened with), /session (spare, guarded) ]
 *
 * A browser back pops the spare entry and lands on the one beneath. The screen
 * is still mounted (same route), so nothing is lost. We notice the landing,
 * call `onBack`, and if the person is still on this screen we put the spare
 * entry back. Every handled back is therefore one pop and one push: the history
 * never grows, however many times back is pressed.
 *
 * `App.tsx` uses `BrowserRouter`, so react-router's `useBlocker` (data routers
 * only) is not available. This is the equivalent for a plain router.
 *
 * @param onBack      Called when the person presses browser back. Return `true`
 *                    if they are still on this screen (stepped back, or were
 *                    shown a confirmation); return `false` if the handler has
 *                    navigated away, so no spare entry is put back.
 * @param entryState  The navigation state the spare entry should carry, without
 *                    the guard flag. Read each time an entry is written, so it
 *                    can change as the screen learns more (for example the id
 *                    of a session once it has been created). A reload restores
 *                    the current entry's state, so this is what a reload sees.
 */
export function useBrowserBack(onBack: () => boolean, entryState: () => Record<string, unknown>) {
  const location = useLocation()
  const navigationType = useNavigationType()
  const navigate = useNavigate()

  const guarded = (location.state as { backGuard?: boolean } | null)?.backGuard === true

  // The latest callbacks, so the effect below never runs against a stale
  // closure over the screen's step state.
  const onBackRef = useRef(onBack)
  const entryStateRef = useRef(entryState)
  useEffect(() => {
    onBackRef.current = onBack
    entryStateRef.current = entryState
  })

  // True once this mounted screen has sat on its spare entry. Until then,
  // landing on an unguarded entry means "just opened", not "back was pressed".
  // (A full page load also reports POP, so the navigation type alone cannot
  // tell the two apart.)
  const armed = useRef(false)
  // The entry already dealt with, so React StrictMode's second effect run in
  // development does not push a second spare entry.
  const handledKey = useRef<string | null>(null)

  useEffect(() => {
    if (guarded) {
      armed.current = true
      // Back on the spare entry, so the entry beneath may be visited again
      // (it keeps its key) and must be dealt with afresh when it is.
      handledKey.current = null
      return
    }
    if (handledKey.current === location.key) return
    handledKey.current = location.key

    const backPressed = armed.current && navigationType === 'POP'
    if (backPressed && !onBackRef.current()) return

    navigate(location.pathname + location.search, {
      state: { ...entryStateRef.current(), backGuard: true },
    })
  }, [guarded, location.key, location.pathname, location.search, navigationType, navigate])
}
